import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';

import {LOAD_BAR} from '../../../constants/loadBar';
import {RECAPTCHA_ACTIONS} from '../../../constants/recaptcha';
import {PSR_TYPES} from '../../../constants/typesMessages';
import {useGetConditions} from '../../../hooks/useGetConditions';
import {useGetProductsCnae} from '../../../hooks/useGetProductsCnae';
import {useRecaptchaProtection} from '../../../hooks/useRecaptchaProtection';
import {sendAddresses} from '../../../services/Api';
import {showErrors} from '../../../services/errorsMessages';
import {addTypeToAddressFromBureau} from '../../../services/flowAddress';
import {HideButtonWhenLogout} from '../../../shared/components/HideButtonWhenLogout';
import {useChatStore} from '../../../store/slices/chatStore';
import {useEcStore} from '../../../store/slices/ecStore';
import {useInteractionStore} from '../../../store/slices/interactionStore';
import {capitalizeFirstLetterOfArray} from '../../../utils';
import './MessageAddressType.style.scss';

const MessageAddressType = (props) => {
  const index = props.content.index;

  // CRÍTICO: Sempre sincroniza com o Zustand para evitar "vazamento" de tipos entre endereços
  const currentAddressTypes = useEcStore(
    (state) => state.ec.addresses[index]?.types || []
  );
  const [isEscritorioClicked, setIsEscritorioClicked] = useState(false);
  const [isLojaClicked, setIsLojaClicked] = useState(false);
  const [isCorrespondenciaClicked, setIsCorrespondenciaClicked] =
    useState(false);

  const ec = useEcStore((state) => state.ec);
  const ecId = useEcStore((state) => state.ec.id);
  const updateAddress = useEcStore((state) => state.updateAddress);
  const sendMessage = useChatStore((state) => state.sendMessage);
  const responseTypeChange = useChatStore((state) => state.responseTypeChange);
  const setMessages = useChatStore((state) => state.setMessages);
  const setChatStageFlow = useChatStore((state) => state.setChatStageFlow);
  const setDotTyping = useInteractionStore((state) => state.setDotTyping);
  const {executeWithProtection} = useRecaptchaProtection();
  useGetProductsCnae();
  useGetConditions();

  /**
   * SEGURANÇA: Sincroniza estado local com Zustand APENAS quando index muda (novo endereço)
   * Garante que cada endereço tenha seus próprios tipos, sem "vazamento"
   * Não inclui currentAddressTypes nas dependências para não resetar durante interação do usuário
   */
  useEffect(() => {
    setIsEscritorioClicked(currentAddressTypes.includes('ESCRITORIO'));
    setIsLojaClicked(currentAddressTypes.includes('LOJA'));
    setIsCorrespondenciaClicked(
      currentAddressTypes.includes('CORRESPONDENCIA')
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  /**
   * useEffect no momento que os botões de Correspondencia, Escritório ou loja forem clicados
   * esse use será chamado e definirá se omite ou faz o botão salvar aparecer.
   */
  useEffect(() => {
    if (!isLojaClicked && !isCorrespondenciaClicked && !isEscritorioClicked) {
      if (document.getElementById('btn-address-save'))
        document.getElementById('btn-address-save').style.display = 'none';
    } else if (document.getElementById('btn-address-save'))
      document.getElementById('btn-address-save').style.display = 'block';
  }, [isCorrespondenciaClicked, isEscritorioClicked, isLojaClicked]);

  /**
   * Declaração dos ClassName para os Botões que define se highlight do botão será ativado ou não
   */
  const unSelectedButton = 'btn-address-type';
  const selectedButton = 'btn-address-type-selected';

  /**
   *Função chamada pelo onchange quando selecionado um dos Botões (Escritório, Loja ou Correspondência)
   * após selecionado o botão passaremos como parametro o name do botão formatando o name para maiusculo.
   */
  const addressUpdateType = ({target: {name}}) => {
    changeStatusOfButtonType(name.toUpperCase());
  };

  /**
   * Função seta através de hooks se botão foi selecionado ou não
   */
  const changeStatusOfButtonType = (typeButton) => {
    switch (typeButton) {
      case 'LOJA':
        setIsLojaClicked(!isLojaClicked);
        break;
      case 'CORRESPONDENCIA':
        setIsCorrespondenciaClicked(!isCorrespondenciaClicked);
        break;
      case 'ESCRITORIO':
        setIsEscritorioClicked(!isEscritorioClicked);
        break;
      default:
        break;
    }
  };
  /**
   * Função chamada quando o usuario selecionar o botão salvar.
   * função coleta tipos com o status = a true no hook, seta os tipos no Zustand,
   * formata e ordena texto para apresentação na tela.
   */
  const saveTypeOfAddress = () => {
    // 1. Coleta tipos selecionados e ordena alfabeticamente
    const selectedTypes = getTypesClicked().toSorted((a, b) =>
      a.localeCompare(b)
    );

    // VALIDAÇÃO: Garante que pelo menos um tipo foi selecionado
    if (selectedTypes.length === 0) {
      console.warn('Nenhum tipo de endereço selecionado');
      return;
    }

    // 2. CRÍTICO: Atualiza APENAS o endereço no índice correto, preservando outros endereços
    const updatedAddresses = ec.addresses.map((addr, idx) =>
      idx === index ? {...addr, types: selectedTypes} : addr
    );

    // 3. Persiste no Zustand
    updateAddress(updatedAddresses);

    // 4. UI feedback
    document.getElementById('button-address-type').classList = 'hidden';

    // 5. Mostra mensagem ao usuário
    const displayTypes = capitalizeFirstLetterOfArray(selectedTypes);
    const displayText = displayTypes.map((t) =>
      t === 'Correspondencia' ? 'Receita Federal' : t
    );
    sendMessage({
      text: displayText.join(', '),
      type: 'response-sent'
    });

    // 6. Determina próximo passo do fluxo
    const nextMessage = addTypeToAddressFromBureau(selectedTypes, index);
    addressDispatchHandler(nextMessage);
  };
  /**
   * Após selecionar o botão salvar a função abaixo será responsável em criar um array com os tipos
   * que estiverem com seu valor = true no hook.
   */
  const getTypesClicked = () => {
    const arrayFromLocalState = [];
    if (isEscritorioClicked) {
      arrayFromLocalState.push('ESCRITORIO');
    }
    if (isCorrespondenciaClicked) {
      arrayFromLocalState.push('CORRESPONDENCIA');
    }
    if (isLojaClicked) {
      arrayFromLocalState.push('LOJA');
    }
    return arrayFromLocalState;
  };

  const addressDispatchHandler = (message) => {
    if (message.type === 'message-receive-address-type') {
      let showAddressBeforeType = {
        type: 'start-addresses-flow',
        index: index,
        step: 'address'
      };
      sendMessage(showAddressBeforeType);
    } else if (message.send) {
      // CRITICAL: Pega o estado atualizado do Zustand
      const currentAddresses = useEcStore.getState().ec.addresses;
      setDotTyping(true);
      executeWithProtection(
        (payload, token) => sendAddresses(ecId, payload, token),
        RECAPTCHA_ACTIONS.DATA_UPDATE,
        currentAddresses
      )
        .then(function () {
          setDotTyping(false);
          message = {
            type: PSR_TYPES.PSR_PAYMENT
          };
          sendMessage(message);
          setChatStageFlow(LOAD_BAR.ADDRESS);
        })
        .catch(function (error) {
          setDotTyping(false);
          const showResponse = showErrors(error, 'endereços');
          setMessages(showResponse.messages);
        });
    } else {
      sendMessage(message);
      if (message.responseType) {
        responseTypeChange(message.responseType);
      }
    }
  };

  const messageType = props.content.text;

  return (
    <div>
      <div className="message-line">
        <div className="message-receive" data-aut-test="message-receive-text">
          <div className="message-div">
            <span>{messageType} </span>
          </div>
        </div>
      </div>
      <HideButtonWhenLogout>
        <div
          className="message-line"
          id="button-address-type"
          data-aut-test="button-address-type"
        >
          <div className="div-button-address-type">
            <button
              className={isLojaClicked ? selectedButton : unSelectedButton}
              onClick={addressUpdateType}
              key="loja"
              name="loja"
              id="btn-address-loja"
              data-aut-test="btn-address-store"
              data-testid="btn-address-store"
            >
              Loja
            </button>
            <button
              className={
                isCorrespondenciaClicked ? selectedButton : unSelectedButton
              }
              onClick={addressUpdateType}
              key="correspondencia"
              name="correspondencia"
              id="btn-address-correspondencia"
              data-aut-test="btn-address-correspondence"
              data-testid="btn-address-correspondence"
            >
              Receita Federal
            </button>
            <button
              onClick={saveTypeOfAddress}
              className="btn-address-save-type"
              id="btn-address-save"
              data-aut-test="btn-address-save"
              data-testid="btn-address-save"
              style={{display: 'none'}}
            >
              Salvar
            </button>
          </div>
        </div>
      </HideButtonWhenLogout>
    </div>
  );
};

MessageAddressType.propTypes = {
  content: PropTypes.object
};

export default MessageAddressType;
